<script setup>
import { ref, defineProps, defineEmits, watch, computed, onMounted } from 'vue';
import { usePedidoStore } from '@/stores/pedido';
import { useClienteStore } from '@/stores/cliente';
import useFormatters from '@/composables/useFormatters';

const { formatMoeda } = useFormatters();

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  revenda: {
    type: Object,
    default: () => ({})
  },
  endereco: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['close', 'success', 'update:show', 'copy-text']);

const pedidoStore = usePedidoStore();
const clienteStore = useClienteStore();

// Dados do formulário
const formData = ref({
  nome_cliente: '',
  telefone_cliente: '',
  email_cliente: '',
  numero_entrega: '',
  complemento_entrega: '',
  idformapagamento: null,
  quantidade: 1,
  qtddias: '',
  dtprevisao: null,
  horaprevisao: null,
  troco: '',
  whatsapp: false, // Novo campo para indicar se a venda foi feita pelo WhatsApp
  cpf_cliente: '' // CPF do cliente para Gás do Povo
});

// Validação de formulário
const formErrors = ref({
  nome_cliente: '',
  telefone_cliente: '',
  email_cliente: '',
  numero_entrega: '',
  dtprevisao: '',
  horaprevisao: '',
  idformapagamento: '',
  qtddias: '',
  troco: '',
  cpf_cliente: ''
});

// Estados de feedback
const loadingSubmit = ref(false);
const pedidoSucesso = ref(false);
const pedidoError = ref('');

// Cliente encontrado da loja
const clienteEncontrado = computed(() => clienteStore.cliente);

// Verificar se é Gás do Povo
const isGasDoPovo = computed(() => {
  return props.revenda?.gas_do_povo === true;
});

// Data mínima para agendamento (hoje)
const dataMinima = computed(() => {
  const hoje = new Date();
  const year = hoje.getFullYear();
  const month = String(hoje.getMonth() + 1).padStart(2, '0');
  const day = String(hoje.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
});

// Função para obter a data atual em formato YYYY-MM-DD
const getDataAtual = () => {
  const hoje = new Date();
  const year = hoje.getFullYear();
  const month = String(hoje.getMonth() + 1).padStart(2, '0');
  const day = String(hoje.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Função para obter a hora atual + 30 minutos em formato HH:MM
const getHoraAtualMais30Min = () => {
  const agora = new Date();
  agora.setMinutes(agora.getMinutes() + 30); // Adiciona 30 minutos
  
  const hours = String(agora.getHours()).padStart(2, '0');
  const minutes = String(agora.getMinutes()).padStart(2, '0');
  
  return `${hours}:${minutes}`;
};

// Definir valores padrão para data e hora
const setDefaultDateTime = () => {
  formData.value.dtprevisao = getDataAtual();
  formData.value.horaprevisao = getHoraAtualMais30Min();
};

// Observa quando o modal é aberto para definir valores padrão
watch(() => props.show, (isOpen) => {
  if (isOpen) {
    setDefaultDateTime();
  }
}, { immediate: true });

// Verifica se todos os campos obrigatórios estão preenchidos
const isFormValid = computed(() => {
  const camposBasicos = (
    formData.value.nome_cliente && 
    formData.value.telefone_cliente && 
    formData.value.telefone_cliente.length >= 14 &&
    formData.value.numero_entrega && 
    formData.value.idformapagamento &&
    formData.value.dtprevisao &&
    formData.value.horaprevisao
  );
  
  // Se for Gás do Povo, também exigir CPF válido
  if (isGasDoPovo.value) {
    return camposBasicos && formData.value.cpf_cliente && validarCPF(formData.value.cpf_cliente);
  }
  
  return camposBasicos;
});

// Computar preço baseado na forma de pagamento selecionada
const precoAtual = computed(() => {
  // Se nenhuma forma de pagamento selecionada, retorna o valor em dinheiro
  if (!formData.value.idformapagamento) {
    return props.revenda.valor;
  }
  
  if (formData.value.idformapagamento === 1 || formData.value.idformapagamento === 4) {
    return props.revenda.valor;
  }
  
  // Cartão de crédito/débito (outros fpg)
  return props.revenda.valorportaria || props.revenda.valor;
});

// Computed property to determine if discount applies
const temDesconto = computed(() => {
  return props.revenda.statusdesconto === 1 && formData.value.quantidade >= 2;
});

// Compute the discount amount
const valorDesconto = computed(() => {
  if (temDesconto.value) {
    return parseFloat(props.revenda.valordesconto || 0);
  }
  return 0;
});

// Preço total computado baseado na quantidade de produtos
const precoTotal = computed(() => {
  let total = precoAtual.value * formData.value.quantidade;
  
  // Apply discount if applicable (only once, regardless of quantity)
  if (temDesconto.value) {
    total -= valorDesconto.value;
  }
  
  return total;
});

// Original price without discount (for display purposes)
const precoOriginalTotal = computed(() => {
  return precoAtual.value * formData.value.quantidade;
});

// Verificar se o método de pagamento selecionado é dinheiro
const isPagamentoEmDinheiro = computed(() => {
  return formData.value.idformapagamento === 1;
});

// Função para validar CPF
const validarCPF = (cpf) => {
  if (!cpf) return false;
  
  // Remove pontuação
  cpf = cpf.replace(/[^\d]/g, '');
  
  // Verifica se tem 11 dígitos
  if (cpf.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  
  // Validação do primeiro dígito verificador
  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let resto = soma % 11;
  let digito1 = resto < 2 ? 0 : 11 - resto;
  
  if (digito1 !== parseInt(cpf.charAt(9))) return false;
  
  // Validação do segundo dígito verificador
  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(cpf.charAt(i)) * (11 - i);
  }
  resto = soma % 11;
  let digito2 = resto < 2 ? 0 : 11 - resto;
  
  return digito2 === parseInt(cpf.charAt(10));
};

// Função para limpar pontuação do CPF
const limparCPF = (cpf) => {
  return cpf ? cpf.replace(/[^\d]/g, '') : '';
};

// Validação visual do CPF em tempo real
const cpfStatus = computed(() => {
  if (!formData.value.cpf_cliente || formData.value.cpf_cliente.length === 0) {
    return { valido: null, mensagem: '' };
  }
  
  const cpfLimpo = limparCPF(formData.value.cpf_cliente);
  
  // CPF incompleto
  if (cpfLimpo.length < 11) {
    return { valido: null, mensagem: 'Digite o CPF completo' };
  }
  
  // CPF completo - validar
  const isValido = validarCPF(formData.value.cpf_cliente);
  return {
    valido: isValido,
    mensagem: isValido ? 'CPF válido' : 'CPF inválido'
  };
});

// Ícone para mostrar no campo de CPF
const cpfIcon = computed(() => {
  if (cpfStatus.value.valido === null) return 'mdi-card-account-details';
  return cpfStatus.value.valido ? 'mdi-check-circle' : 'mdi-close-circle';
});

// Cor do ícone do CPF
const cpfIconColor = computed(() => {
  if (cpfStatus.value.valido === null) return '';
  return cpfStatus.value.valido ? 'success' : 'error';
});

// Verificar se o método de pagamento selecionado é cartão de crédito
// e se tem parcelamento disponível
const temParcelamento = computed(() => {
  return props.revenda.statusparcela == 1 && 
         props.revenda.parcelamento && 
         props.revenda.qtdParcela;
});

// Opções de parcelamento disponíveis
const opcoesParcelamento = computed(() => {
  if (!temParcelamento.value) {
    return [];
  }
  
  const qtdMaxParcelas = props.revenda.qtdParcela.qtdparcela;
  const opcoes = [];
  
  // Calculate the base price (card payment value)
  const precoBase = props.revenda.valorportaria || props.revenda.valor;
  
  // Calculate the regular price for all items
  const totalSemDesconto = precoBase * formData.value.quantidade;
  
  // Calculate discount ratio if applicable
  const descontoProporcional = temDesconto.value 
    ? valorDesconto.value / totalSemDesconto 
    : 0;
  
  for (let i = 1; i <= qtdMaxParcelas; i++) {
    // Get the installment value per unit from the parcelamento data
    const valorParcelaUnitaria = props.revenda.parcelamento.parcelamento[i-1];
    
    // Calculate total installment value based on quantity
    let valorParcelaTotal = valorParcelaUnitaria * formData.value.quantidade;
    
    // Apply discount proportionally if applicable
    if (temDesconto.value) {
      valorParcelaTotal = valorParcelaTotal - (valorParcelaTotal * descontoProporcional);
    }
    
    opcoes.push({
      value: i,
      title: `${i}x de ${formatMoeda(valorParcelaTotal)} ${props.revenda.parcelamento.juros && i > 1 ? '(com juros)' : '(sem juros)'}`
    });
  }
  
  return opcoes;
});

// Variável para armazenar o número de parcelas selecionado
const parcelasSelecionadas = ref(1);

// Observa mudanças no cliente para preencher os dados
watch(() => clienteEncontrado.value, (cliente) => {
  if (cliente) {
    formData.value.nome_cliente = cliente.nome || '';
    formData.value.telefone_cliente = cliente.telefone1 || '';
    formData.value.email_cliente = cliente.email || '';
    formData.value.qtddias = cliente.qtddias || '';
    formData.value.numero_entrega = cliente.numero || '';
    formData.value.complemento_entrega = cliente.complemento || '';
  }
}, { immediate: true });

// Observa mudanças no endereço selecionado
watch(() => props.endereco, (endereco) => {
  if (endereco) {
    formData.value.numero_entrega = endereco.numero || '';
  }
}, { immediate: true });

// Buscar cliente pelo telefone
const buscarClientePorTelefone = async () => {
  if (formData.value.telefone_cliente.length >= 14) {
    await clienteStore.buscarClientePorTelefone(formData.value.telefone_cliente);
    clienteStore.limparErros();
  }
};

// Validar campos do formulário
const validarCampos = () => {
  let isValid = true;
  formErrors.value = {
    nome_cliente: '',
    telefone_cliente: '',
    email_cliente: '',
    numero_entrega: '',
    dtprevisao: '',
    horaprevisao: '',
    idformapagamento: '',
    qtddias: '',
    troco: '',
    cpf_cliente: ''
  };

  if (!formData.value.nome_cliente) {
    formErrors.value.nome_cliente = 'Nome é obrigatório';
    isValid = false;
  }

  if (!formData.value.telefone_cliente) {
    formErrors.value.telefone_cliente = 'Telefone é obrigatório';
    isValid = false;
  } else if (formData.value.telefone_cliente.length < 14) {
    formErrors.value.telefone_cliente = 'Telefone inválido';
    isValid = false;
  }
  
  // Validação de email (se fornecido)
  if (formData.value.email_cliente && !validarEmail(formData.value.email_cliente)) {
    formErrors.value.email_cliente = 'Email inválido';
    isValid = false;
  }

  if (!formData.value.numero_entrega) {
    formErrors.value.numero_entrega = 'Número é obrigatório';
    isValid = false;
  }

  if (!formData.value.dtprevisao) {
    formErrors.value.dtprevisao = 'Data é obrigatória';
    isValid = false;
  }

  if (!formData.value.horaprevisao) {
    formErrors.value.horaprevisao = 'Hora é obrigatória';
    isValid = false;
  }

  if (!formData.value.idformapagamento) {
    formErrors.value.idformapagamento = 'Forma de pagamento é obrigatória';
    isValid = false;
  }

  // Validação do troco (apenas para pagamento em dinheiro)
  if (isPagamentoEmDinheiro.value && formData.value.troco) {
    const trocoValue = parseFloat(formData.value.troco);
    const valorPedido = precoTotal.value;
    
    if (isNaN(trocoValue) || trocoValue <= valorPedido) {
      formErrors.value.troco = 'Valor para troco deve ser maior que o valor do pedido';
      isValid = false;
    }
  }

  // Validação do CPF (apenas para Gás do Povo)
  if (isGasDoPovo.value) {
    if (!formData.value.cpf_cliente) {
      formErrors.value.cpf_cliente = 'CPF é obrigatório para Gás do Povo';
      isValid = false;
    } else if (!validarCPF(formData.value.cpf_cliente)) {
      formErrors.value.cpf_cliente = 'CPF inválido';
      isValid = false;
    }
  }

  return isValid;
};

// Função para validar formato de email
const validarEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};

// Formato ISO da data e hora para envio
const formatarDataHora = () => {
  if (formData.value.dtprevisao && formData.value.horaprevisao) {
    return `${formData.value.dtprevisao} ${formData.value.horaprevisao}:00`;
  }
  return null;
};

// Add ref for confirmation dialog
const confirmDialog = ref(false);

// Submeter o formulário para realizar o pedido
// Modified realizarPedido function to show confirmation dialog if revenda is closed
const realizarPedido = async () => {
  if (!validarCampos()) {
    return;
  }

  // If the revenda is disabled for orders, show error
  if (isPedidoDesabilitado.value) {
    pedidoError.value = 'Esta revenda não está disponível para pedidos no momento.';
    return;
  }

  // If the revenda is closed but not disabled, show confirmation dialog
  if (!isRevendaAberta.value) {
    confirmDialog.value = true;
    return;
  }

  // Otherwise proceed with order
  submitOrder();
};

// New function to actually submit the order
const submitOrder = async () => {
  loadingSubmit.value = true;
  pedidoError.value = '';

  try {
    // Preparar os dados para o pedido
    const payload = {
      idrevenda: props.revenda.idrevenda,
      idproduto: props.revenda.idproduto,
      idformapagamento: formData.value.idformapagamento,
      quantidade: formData.value.quantidade,
      qtddias: formData.value.qtddias || null,
      dtprevisao: formatarDataHora(),
      logradouro_entrega: props.endereco.logradouro,
      numero_entrega: formData.value.numero_entrega,
      complemento_entrega: formData.value.complemento_entrega || '',
      bairro_entrega: props.endereco.bairro,
      cidade_entrega: props.endereco.cidade,
      estado_entrega: props.endereco.estado,
      lat_entrega: props.endereco.latitude,
      long_entrega: props.endereco.longitude,
      origem: 4, // Origem: web
      nome_cliente: formData.value.nome_cliente,
      telefone_cliente: formData.value.telefone_cliente,
      email_cliente: formData.value.email_cliente || '',
      qtd_parcela: temParcelamento.value ? parcelasSelecionadas.value : undefined,
      troco: isPagamentoEmDinheiro.value && formData.value.troco ? formData.value.troco : undefined,
      whatsapp: formData.value.whatsapp ? 1 : 0, // Adicionar o campo whatsapp ao payload
      cpf_cliente: isGasDoPovo.value && formData.value.cpf_cliente ? limparCPF(formData.value.cpf_cliente) : undefined // CPF sem pontuação para Gás do Povo
    };

    // Enviar o pedido
    const response = await pedidoStore.realizarPedido(payload);
    
    // Verificar se a resposta está no formato esperado
    if (response && response.status === "success" && response.data && response.data.numero_pedido > 0) {
      pedidoSucesso.value = true;
      
      // Emitir evento de sucesso com os dados corretos
      emit('success', response.data);
      
      // Removida a linha que fechava o modal automaticamente após 3 segundos
      // Agora o usuário precisa fechar manualmente
    } else {
      // Se a resposta não estiver no formato esperado, tratar como erro
      throw new Error(response?.message || 'Resposta inesperada do servidor');
    }
    
  } catch (error) {
    console.error('Erro ao realizar pedido:', error);
    
    // Se o erro for 409 (Conflict), mostrar mensagem específica sobre pedido já existente
    if (error.response && error.response.status === 409) {
      pedidoError.value = error.response.data?.message || 
        'Cliente já possui um pedido em aberto. Não é possível realizar um novo pedido.';
    } else {
      pedidoError.value = error.message || 'Ocorreu um erro ao realizar o pedido. Tente novamente.';
    }
  } finally {
    loadingSubmit.value = false;
    confirmDialog.value = false;
  }
};

// Função para copiar o número do pedido - modificada para copiar a mensagem completa
const copiarNumeroPedido = () => {
  try {
    const numeroPedido = pedidoStore.pedidoRealizado?.data?.numero_pedido;
    if (numeroPedido) {
      const mensagemCompleta = `O pedido número ${numeroPedido} foi realizado com sucesso.Obrigado! \nAcompanhe seu pedido pelo App. Clique no link para baixar: \nhttp://precodogas.com.br/baixar-app/64`;
      // Emitir um evento para o componente pai lidar com a cópia
      emit('copy-text', {
        text: 'Mensagem do pedido',
        value: mensagemCompleta
      });
    }
  } catch (err) {
    console.error('Erro ao copiar mensagem do pedido:', err);
  }
};

// Fechar o modal
const fecharModal = () => {
  emit('update:show', false);
  emit('close');
  resetarForm();
};

// Resetar o formulário
const resetarForm = () => {
  formData.value = {
    nome_cliente: '',
    telefone_cliente: '',
    email_cliente: '',
    numero_entrega: '',
    complemento_entrega: '',
    idformapagamento: null,
    quantidade: 1,
    qtddias: '',
    dtprevisao: null,
    horaprevisao: null,
    troco: '',
    whatsapp: false, // Resetar também o campo whatsapp
    cpf_cliente: '' // Resetar o CPF
  };
  
  // Definir valores padrão para data e hora novamente
  setDefaultDateTime();
  
  pedidoSucesso.value = false;
  pedidoError.value = '';
};

// Inicializar valores padrão quando o componente for montado
onMounted(() => {
  if (props.show) {
    setDefaultDateTime();
  }
});

// Function to abbreviate day names - move it outside computed property to make it accessible in template
const abreviarDia = (dia) => {
  const abreviacoes = {
    'Segunda a Sexta': 'Seg-Sex',
    'Sábado': 'Sab',
    'Domingo': 'Dom'
  };
  return abreviacoes[dia] || dia;
};

// Verificar se está desabilitado para pedido (ateAmanha ou praSempre)
const isPedidoDesabilitado = computed(() => {
  if (props.revenda.aberto === 0 && 
      props.revenda.horario_da_reativacao_automatica && 
      props.revenda.horario_da_reativacao_automatica.observacao) {
    const observacao = props.revenda.horario_da_reativacao_automatica.observacao;
    return observacao === "ateAmanha" || observacao === "praSempre";
  }
  return false;
});

// Verifica se a revenda está aberta - modificado para dar prioridade ao campo aberto
const isRevendaAberta = computed(() => {
  // First check if the revenda is explicitly marked as closed
  if (props.revenda.aberto === 0) {
    return false;
  }
  
  // If revenda is explicitly marked as open, check if it has disabling reactivation info
  if (isPedidoDesabilitado.value) {
    return false;
  }
  
  // If no explicit closed status, check operating hours as backup
  if (!props.revenda.days || props.revenda.days.length === 0) {
    return true; // If no hours specified but aberto=1, consider it open
  }
  
  const agora = new Date();
  const diaSemana = agora.getDay(); // 0 = domingo, 1 = segunda, ..., 6 = sábado
  const horaAtual = agora.getHours();
  const minutoAtual = agora.getMinutes();
  const horaAtualTotal = horaAtual + (minutoAtual / 60);
  
  // Mapear dias da semana para os nomes usados na API
  const diasSemanaMap = {
    0: 'Domingo',
    1: 'Segunda a Sexta',
    2: 'Segunda a Sexta',
    3: 'Segunda a Sexta',
    4: 'Segunda a Sexta',
    5: 'Segunda a Sexta',
    6: 'Sábado'
  };
  
  // Verificar se há um horário para o dia atual
  const diaAtual = diasSemanaMap[diaSemana];
  const horarioHoje = props.revenda.days.find(day => day.descricao === diaAtual);
  
  if (!horarioHoje) return true; // If no hours for today but aberto=1, consider it open
  
  // Converter horas de string "HH:MM" para número decimal
  const converterHoraParaDecimal = (horaString) => {
    const [horas, minutos] = horaString.split(':').map(Number);
    return horas + (minutos / 60);
  };
  
  const horaInicio = converterHoraParaDecimal(horarioHoje.horainicio);
  const horaFim = converterHoraParaDecimal(horarioHoje.horafim);
  
  // Verificar se a hora atual está dentro do horário de funcionamento
  return horaAtualTotal >= horaInicio && horaAtualTotal <= horaFim;
});

// Format reactivation date to a readable format
const formatarDataReativacao = (dataStr) => {
  if (!dataStr) return '';
  
  try {
    const data = new Date(dataStr);
    
    // Format date
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    
    // Format time
    const hora = String(data.getHours()).padStart(2, '0');
    const minuto = String(data.getMinutes()).padStart(2, '0');
    
    return `${dia}/${mes}/${ano} às ${hora}:${minuto}`;
  } catch (error) {
    console.error("Erro ao formatar data de reativação:", error);
    return dataStr; // Return original string if error
  }
};

// Check if revenda has reactivation information
const temInfoReativacao = computed(() => {
  return !isRevendaAberta.value && 
         props.revenda.horario_da_reativacao_automatica && 
         props.revenda.horario_da_reativacao_automatica.reativacao_em &&
         !isPedidoDesabilitado.value;
});

// Get formatted reactivation time
const dataReativacao = computed(() => {
  if (!temInfoReativacao.value) return '';
  return formatarDataReativacao(props.revenda.horario_da_reativacao_automatica.reativacao_em);
});

// Get reactivation minutes
const minutosReativacao = computed(() => {
  if (!temInfoReativacao.value) return '';
  return props.revenda.horario_da_reativacao_automatica.reativado_em_minutos || '';
});

// Get reactivation observation (reason/time)
const obsReativacao = computed(() => {
  if (!temInfoReativacao.value) return '';
  return props.revenda.horario_da_reativacao_automatica.observacao || '';
});

// Format agendamento message
const mensagemAgendamento = computed(() => {
  if (!isRevendaAberta.value) {
    if (isPedidoDesabilitado.value) {
      const observacao = props.revenda.horario_da_reativacao_automatica.observacao;
      return {
        texto: observacao === "ateAmanha" 
          ? "Indisponível até amanhã" 
          : "Revenda permanentemente fechada",
        observacao: observacao,
        temReativacao: false,
        desabilitado: true
      };
    } else if (temInfoReativacao.value) {
      return {
        texto: `Agendar pedido. Reativa em ${minutosReativacao.value} minutos`,
        observacao: obsReativacao.value,
        temReativacao: true,
        desabilitado: false
      };
    } else {
      return {
        texto: "Agendar pedido",
        temReativacao: false,
        desabilitado: false
      };
    }
  }
  return null;
});

// Função para emitir evento para copiar ID do pedido
const emitCopyPedidoId = (pedidoId) => {
  emit('copy-text', {
    text: 'Número do pedido em aberto',
    value: pedidoId
  });
};

// Compute payment option display price with discount
const getValorPorMetodoPagamento = (forma) => {
  // Base price based on payment method
  const basePrice = (forma.id === 1 || forma.id === 4) 
    ? props.revenda.valor 
    : (props.revenda.valorportaria || props.revenda.valor);
    
  // Total price for quantity
  let total = basePrice * formData.value.quantidade;
  
  // Apply discount if applicable
  if (temDesconto.value) {
    total -= valorDesconto.value;
  }
  
  return formatMoeda(total);
};
</script>

<template>
  <v-dialog
    :model-value="show"
    @update:model-value="(val) => emit('update:show', val)"
    max-width="90%"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="text-h5 bg-primary text-white">
        {{ pedidoSucesso ? 'Pedido Realizado' : 'Finalizar Pedido' }}
        <v-spacer></v-spacer>
      </v-card-title>

      <v-card-text class="pa-4 pt-6">
        <div v-if="pedidoSucesso" class="text-center py-6">
          <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
          <h2 class="text-h5 mb-2">Pedido realizado com sucesso!</h2>
          <p class="text-body-1 mb-4">
            O pedido número {{ pedidoStore.pedidoRealizado?.data?.numero_pedido }} foi realizado com sucesso. Obrigado!
          </p>
          
          <v-btn 
            color="primary" 
            variant="outlined"
            prepend-icon="mdi-content-copy"
            @click="copiarNumeroPedido"
            class="mb-4"
          >
            Copiar mensagem
          </v-btn>
          
          <div class="mt-6">
            <v-btn 
              color="primary" 
              @click="fecharModal"
            >
              Fechar
            </v-btn>
          </div>
        </div>

        <div v-else>
          <!-- Alert for disabled revenda -->
          <v-alert
            v-if="isPedidoDesabilitado"
            type="error"
            variant="tonal"
            class="mb-4"
            icon="mdi-block-helper"
            border="start"
            prominent
          >
            <div class="text-h6">Revenda indisponível</div>
            <p>
              {{ props.revenda.horario_da_reativacao_automatica.observacao === "ateAmanha" 
                  ? "Esta revenda está temporariamente indisponível até amanhã." 
                  : "Esta revenda está permanentemente fechada e não recebe novos pedidos." }}
            </p>
          </v-alert>

          <!-- Alert for closed revenda - display more prominently -->
          <v-alert
            v-else-if="!isRevendaAberta"
            type="warning"
            variant="tonal"
            class="mb-4 warning-alert-custom"
            icon="mdi-store-clock"
            border="start"
            prominent
          >
            <div class="text-h6 text-black">Revenda fechada no momento</div>
            <p class="text-black">Esta revenda não está funcionando agora. Seu pedido deve ser agendado para quando a revenda abrir.</p>
            <div v-if="temInfoReativacao" class="mt-2">
              <div class="text-black"><strong>Reabertura prevista:</strong> em {{ minutosReativacao }} minutos</div>
              <div v-if="obsReativacao" class="text-caption font-italic text-grey-darken-2">{{ obsReativacao }}</div>
            </div>
          </v-alert>

          <v-alert
            v-if="pedidoError"
            type="error"
            variant="tonal"
            closable
            class="mb-4"
            icon="mdi-alert-circle"
          >
            <div v-if="pedidoError.includes('pedido em aberto')">
              <strong>Não foi possível realizar o pedido</strong>
              <div class="mt-1">{{ pedidoError }}</div>
            </div>
            <div v-else>
              {{ pedidoError }}
            </div>
          </v-alert>

          <v-form @submit.prevent="realizarPedido">
            <v-row>
              <!-- COLUNA 1: Dados do cliente e endereço -->
              <v-col cols="12" md="4">
                <v-card variant="outlined" class="pa-3 pb-0 mb-3">
                  <h3 class="text-subtitle-1 font-weight-bold mb-2">Dados do Cliente</h3>
                  <!-- Aviso de pedidos em aberto -->
                  <div v-if="clienteEncontrado && clienteEncontrado.tem_pedido_aberto" 
                       class="open-orders-alert d-flex align-center mb-3">
                    <v-icon color="error" size="small" class="mr-2">mdi-alert-circle</v-icon>
                    <div>
                      <span class="text-error font-weight-medium">Cliente tem pedido(s) em aberto:</span>
                      <div class="d-flex flex-wrap mt-0">
                        <v-chip
                          v-for="pedidoId in clienteEncontrado.pedidos_abertos"
                          :key="pedidoId"
                          color="error"
                          variant="outlined"
                          size="x-small"
                          class="mr-1 mb-1 cursor-pointer"
                          @click="emitCopyPedidoId(pedidoId)"
                          title="Clique para copiar o número do pedido"
                        >
                          #{{ pedidoId }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                  
                  <v-text-field
                    v-model="formData.nome_cliente"
                    label="Nome completo"
                    variant="outlined"
                    density="compact"
                    required
                    :error-messages="formErrors.nome_cliente"
                  ></v-text-field>
                  
                  <v-text-field
                    v-model="formData.telefone_cliente"
                    label="Telefone"
                    variant="outlined"
                    density="compact"
                    v-mask="'(##) #####-####'"
                    required
                    :error-messages="formErrors.telefone_cliente"
                    @blur="buscarClientePorTelefone"
                  ></v-text-field>
                  
                  <v-text-field
                    v-model="formData.email_cliente"
                    label="Email (opcional)"
                    type="email"
                    variant="outlined"
                    density="compact"
                    :error-messages="formErrors.email_cliente"
                    placeholder="cliente@exemplo.com"
                  ></v-text-field>
                  
                  <v-text-field
                    v-model="formData.qtddias"
                    label="Duração do Gás (dias)"
                    type="number"
                    variant="outlined"
                    density="compact"
                    min="1"
                    :error-messages="formErrors.qtddias"
                  ></v-text-field>
                </v-card>
                
                <v-card variant="outlined" class="pa-3 pb-0">
                  <h3 class="text-subtitle-1 font-weight-bold mb-2">Endereço de Entrega</h3>
                  
                  <v-text-field
                    :model-value="endereco.formattedAddress"
                    label="Endereço"
                    variant="outlined"
                    density="compact"
                    readonly
                    disabled
                  ></v-text-field>
                  
                  <v-text-field
                    v-model="formData.numero_entrega"
                    label="Número"
                    variant="outlined"
                    density="compact"
                    required
                    :error-messages="formErrors.numero_entrega"
                    class="flex-grow-0"
                    style="width: 150px;"
                  ></v-text-field>
                  
                  <v-text-field
                    v-model="formData.complemento_entrega"
                    label="Complemento (opcional)"
                    variant="outlined"
                    density="compact"
                    placeholder="Ex: Apto 101, Bloco B"
                    class="flex-grow-1"
                  ></v-text-field>

                </v-card>
              </v-col>
              
              <!-- COLUNA 2: Dados de entrega e horários da revenda -->
              <v-col cols="12" md="4">
                <v-card variant="outlined" class="pa-3 pb-0 mb-3">
                  <h3 class="text-subtitle-1 font-weight-bold mb-2">Dados da Entrega</h3>
                  
                  <v-text-field
                    v-model="formData.dtprevisao"
                    label="Data de entrega"
                    variant="outlined"
                    density="compact"
                    type="date"
                    :min="dataMinima"
                    required
                    :error-messages="formErrors.dtprevisao"
                  ></v-text-field>
                  
                  <v-text-field
                    v-model="formData.horaprevisao"
                    label="Hora de entrega"
                    variant="outlined"
                    type="time"
                    density="compact"
                    required
                    :error-messages="formErrors.horaprevisao"
                  ></v-text-field>
                </v-card>
                
                <v-card variant="outlined" class="pa-3">
                  <!-- Added revenda name to this section -->
                  <h3 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ revenda.revendedor }}
                  </h3>
                  
                  <div class="status-container mb-2">
                    <v-chip
                      :color="isRevendaAberta ? 'success' : (isPedidoDesabilitado ? 'error' : 'warning-alert-custom')"
                      class="font-weight-bold"
                      
                    >
                      <v-icon start>
                        {{ isRevendaAberta 
                            ? 'mdi-store' 
                            : (isPedidoDesabilitado ? 'mdi-block-helper' : 'mdi-store-off') }}
                      </v-icon>
                      {{ isRevendaAberta 
                          ? 'Aberto agora' 
                          : (isPedidoDesabilitado ? 'Indisponível' : 'Fechado agora') }}
                    </v-chip>
                    
                    <!-- Mensagem de Agendamento para revendas fechadas -->
                    <div v-if="mensagemAgendamento" class="mt-3 agendamento-container" 
                         :class="{
                           'com-reativacao': mensagemAgendamento.temReativacao,
                           'desabilitado': mensagemAgendamento.desabilitado
                         }">
                      <div class="agendamento-icon">
                        <v-icon 
                          :color="mensagemAgendamento.desabilitado ? 'error' : 'warning-alert-custom'"
                        >
                          {{ mensagemAgendamento.desabilitado 
                              ? 'mdi-block-helper' 
                              : 'mdi-calendar-clock' }}
                        </v-icon>
                      </div>
                      <div class="agendamento-info">
                        <div class="agendamento-texto">{{ mensagemAgendamento.texto }}</div>
                        <div v-if="mensagemAgendamento.observacao && !mensagemAgendamento.desabilitado" class="agendamento-observacao">
                          &nbsp;{{ mensagemAgendamento.observacao }}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="horarios-container">
                    <div v-for="(day, index) in revenda.days" :key="index" class="horario-item">
                      <div class="horario-dia">{{ abreviarDia(day.descricao) }}</div>
                      <div class="horario-horas">{{ day.horainicio }}-{{ day.horafim }}</div>
                    </div>
                  </div>
                </v-card>
              </v-col>
              
              <!-- COLUNA 3: Quantidade e forma de pagamento -->
              <v-col cols="12" md="4">
                <v-card variant="outlined" class="pa-3 mb-3">
                  <!-- Added product info to this section -->
                  <h3 class="text-subtitle-1 font-weight-bold mb-2">Produto</h3>
                  <div class="text-subtitle-2 mb-3">
                    {{ revenda.fabricante }} - {{ revenda.produto }} {{ revenda.peso }}kg
                  </div>
                  
                  <v-text-field
                    v-model.number="formData.quantidade"
                    label="Quantidade de botijões"
                    type="number"
                    variant="outlined"
                    density="compact"
                    min="1"
                    required
                    placeholder="1"
                  ></v-text-field>
                  
                  <div class="text-subtitle-2 font-weight-bold text-right">
                    Valor unitário: {{ formatMoeda(precoAtual) }}<br>
                    <template v-if="temDesconto">
                      <div class="original-price text-decoration-line-through text-grey">
                        {{ formatMoeda(precoOriginalTotal) }}
                      </div>
                      <div class="discount-info d-flex justify-end align-center text-success text-caption mb-1">
                        <v-icon size="small" color="success" class="mr-1">mdi-tag-outline</v-icon>
                        Desconto no 2º botijão: -{{ formatMoeda(valorDesconto) }}
                      </div>
                    </template>
                    Valor total: <span :class="{'text-success font-weight-bold': temDesconto}">{{ formatMoeda(precoTotal) }}</span>
                  </div>
                </v-card>
                
                <v-card variant="outlined" class="pa-3">
                  <h3 class="text-subtitle-1 font-weight-bold mb-3">Forma de Pagamento</h3>
                  
                  <v-radio-group
                    v-model="formData.idformapagamento"
                    :error-messages="formErrors.idformapagamento"
                  >
                    <v-radio
                      v-for="forma in revenda.fpg"
                      :key="forma.id"
                      :value="forma.id"
                      color="primary"
                      class="mb-1 payment-radio"
                    >
                      <template v-slot:label>
                        <div class="payment-option-container">
                          <div class="payment-option-left">
                            <v-icon size="small" class="mr-2">
                              {{ forma.id === 1 ? 'mdi-cash' : 
                                 forma.id === 2 ? 'mdi-credit-card' : 
                                 forma.id === 3 ? 'mdi-credit-card-outline' : 
                                 forma.id === 4 ? 'mdi-cash-fast' : 'mdi-currency-usd' }}
                            </v-icon>
                            <span>{{ forma.descricao }}</span>
                          </div>
                          <div class="payment-option-right">
                            {{ getValorPorMetodoPagamento(forma) }}
                          </div>
                        </div>
                      </template>
                    </v-radio>
                  </v-radio-group>
                  
                  <!-- Opções adicionais baseadas na forma de pagamento -->
                  <div v-if="isPagamentoEmDinheiro" class="mt-3">
                    <v-text-field
                      v-model="formData.troco"
                      label="Troco para (opcional)"
                      type="number"
                      variant="outlined"
                      density="compact"
                      :error-messages="formErrors.troco"
                      :hint="`Valor do pedido: ${formatMoeda(precoTotal)}`"
                      persistent-hint
                      prefix="R$"
                    ></v-text-field>
                  </div>
                  
                  <div v-if="temParcelamento && formData.idformapagamento === 2" class="mt-3">
                    <v-select
                      v-model="parcelasSelecionadas"
                      :items="opcoesParcelamento"
                      label="Parcelamento"
                      variant="outlined"
                      density="compact"
                    ></v-select>
                  </div>
                </v-card>

                <!-- Campo CPF para Gás do Povo -->
                <v-card v-if="isGasDoPovo" variant="outlined" class="pa-3 mt-4">
                  <h3 class="text-subtitle-1 font-weight-bold mb-3">
                    <v-icon start color="error" size="small">mdi-fire</v-icon>
                    Gás do Povo - CPF do Beneficiário
                  </h3>
                  <v-text-field
                    v-model="formData.cpf_cliente"
                    label="CPF do cliente *"
                    variant="outlined"
                    density="compact"
                    v-mask="'###.###.###-##'"
                    :error-messages="formErrors.cpf_cliente"
                    :hint="cpfStatus.mensagem"
                    :persistent-hint="true"
                    :prepend-inner-icon="cpfIcon"
                    :color="cpfIconColor"
                    required
                  >
                    <template v-slot:append-inner>
                      <v-icon 
                        v-if="cpfStatus.valido !== null"
                        :color="cpfIconColor"
                        size="small"
                      >
                        {{ cpfStatus.valido ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                      </v-icon>
                    </template>
                  </v-text-field>
                </v-card>

                <!-- Checkbox WhatsApp -->
                <v-checkbox
                    v-model="formData.whatsapp"
                    color="success"
                    hide-details
                    density="compact"
                    class="mt-4"
                  >
                    <template v-slot:label>
                      <div class="d-flex align-center">
                      <v-icon start color="success" class="mr-1">mdi-whatsapp</v-icon>
                      <span style="color: green">Venda feita pelo WhatsApp</span>
                      </div>
                    </template>
                  </v-checkbox>
              </v-col>

            </v-row>
            
            <!-- Botões de ação -->
            <div class="d-flex justify-end mt-4">
              <v-btn 
                variant="text" 
                @click="fecharModal" 
                class="me-2"
                :disabled="loadingSubmit"
              >
                Cancelar
              </v-btn>
              <v-btn 
                color="primary" 
                type="submit"
                :loading="loadingSubmit"
                :disabled="!isFormValid || isPedidoDesabilitado"
              >
                {{ isPedidoDesabilitado 
                    ? 'Indisponível' 
                    : (isRevendaAberta ? 'Realizar Pedido' : 'Agendar Pedido') }}
              </v-btn>
            </div>
          </v-form>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
  
  <!-- Confirmation dialog for closed revenda -->
  <v-dialog v-model="confirmDialog" max-width="500px" persistent>
    <v-card>
      <v-card-title class="text-h5 bg-warning-lighten-1 pa-4">
        <v-icon size="large" class="mr-2 text-black">mdi-clock-alert-outline</v-icon>
        <span class="text-black">Confirmação de agendamento</span>
      </v-card-title>
      <v-card-text class="pa-4 pt-6">
        <p class="text-body-1 text-black">
          Esta revenda está <strong>fechada</strong> no momento. Ao prosseguir, seu pedido será agendado para quando a revenda abrir.
        </p>
        <div v-if="temInfoReativacao" class="mt-3 pa-3 bg-grey-lighten-4 rounded">
          <div class="text-black"><strong>Reabertura prevista:</strong> em {{ minutosReativacao }} minutos</div>
          <div v-if="obsReativacao" class="text-caption font-italic text-grey-darken-2">{{ obsReativacao }}</div>
        </div>
        <p class="text-body-2 mt-4 text-black">
          Deseja prosseguir com o agendamento do pedido?
        </p>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="confirmDialog = false" :disabled="loadingSubmit">
          Cancelar
        </v-btn>
        <v-btn 
          color="warning" 
          variant="elevated" 
          @click="submitOrder"
          :loading="loadingSubmit"
        >
          Confirmar Agendamento
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* Removed .revenda-info class as it's no longer needed */

.pedido-numero {
  font-size: 1.2em;
  color: var(--v-primary-base);
}

.horarios-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.horario-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 10px;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}

.horario-dia {
  font-weight: 500;
}

.horario-horas {
  color: #1976d2;
}

.payment-radio {
  width: 100%;
}

/* Fix the payment option container to occupy full width */
.payment-radio :deep(.v-selection-control-group) {
  width: 100%;
}

.payment-radio :deep(.v-selection-control) {
  width: 100%;
}

.payment-radio :deep(.v-selection-control__wrapper) {
  width: auto;
}

.payment-option-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex: 1;
}

.payment-option-left {
  display: flex;
  align-items: center;
}

.payment-option-right {
  font-weight: 500;
  color: var(--v-theme-primary);
  margin-left: auto;
  position: absolute;
  right: 10px;
}

.reativacao-info {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #ED6C02; /* orange-darken-2 */
  background-color: rgba(237, 108, 2, 0.08);
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.reativacao-texto {
  font-weight: 500;
}

.reativacao-obs {
  font-style: italic;
  opacity: 0.9;
}

/* Add custom styling for warning alerts */
.warning-alert-custom {
  color: black !important;
}

.warning-alert-custom .v-icon {
  color: #e65100 !important; /* orange-darken-3 for better contrast */
}

/* Update agendamento container colors for better contrast */
.agendamento-container {
  display: flex;
  align-items: flex-start;
  background-color: rgba(5, 5, 5, 0.15);
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 8px;
}

.agendamento-container.com-reativacao {
  background-color: rgba(5, 5, 5, 0.15);
  border-left: 3px solid #686868;
}

.agendamento-texto {
  font-weight: 600;
  color: #1a1a1a;
  font-size: 1rem;
  line-height: 1.3;
  margin-left: 2px;
}

.agendamento-observacao {
  font-size: 0.875rem;
  color: #424242; /* Dark grey for better contrast */
  margin-top: 4px;
}

/* Animation for warning elements */
@keyframes pulse {
  0% { opacity: 0.8; }
  50% { opacity: 1; }
  100% { opacity: 0.8; }
}

.status-container .v-chip--color-error {
  animation: pulse 2s infinite;
}

.open-orders-alert {
  padding: 8px 12px;
  background-color: rgba(244, 67, 54, 0.08);
  border-radius: 4px;
  border-left: 3px solid #f44336;
}

.cursor-pointer {
  cursor: pointer;
}
</style>